import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import { IntegrationTab } from "../shared/IntegrationTab";
import useApi from "@/hooks/useApi";
import { Severity } from "@velaro/velaro-component-lib";
import useToastAlert from "@/hooks/useToastAlert";
import { IconClose1 } from "@velaro/velaro-component-lib";
import WooCommerceBotKnowledge from "./WooCommerceBotKnowledge";
import WooCommerceBilling from "./WooCommerceBilling";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import IconByName from "@/components/ds/IconByName";
import SkillControlsPanel from "../SkillControlsPanel";
import ShopperProfileTab from "../shared/ShopperProfileTab";
import useAdminFeatures from "@/hooks/useAdminFeatures";
import LaunchWizardButton from "@/components/LaunchWizardButton";
import { SectionCard, PrimaryButton, Divider } from "@/components/ds";
import { Lightbulb, Check } from "@/components/ds/icons";
import useTeams from "@/hooks/useTeams";
import { ensureDefaultRoutingWorkflow } from "@/pages/Messaging/Workflows/defaultWorkflowHelper";

interface ConnectForm {
  storeUrl: string;
  consumerKey: string;
  consumerSecret: string;
}

// ─── AI Tools ─────────────────────────────────────────────────────────────────

const WOO_AI_TOOLS = [
  // Products (7)
  {
    name: "woocommerce_search_products",
    label: "Search products",
    group: "Products",
    description:
      "Search the catalog by keyword. Returns product name, price, stock status, SKU, and URL."
  },
  {
    name: "woocommerce_get_product_by_sku",
    label: "Get product by SKU",
    group: "Products",
    description:
      "Retrieve a specific product using its SKU. Returns full product details."
  },
  {
    name: "woocommerce_get_product_variations",
    label: "Get product variations",
    group: "Products",
    description:
      "List all size/color/option variations for a variable product with per-variant pricing and stock."
  },
  {
    name: "woocommerce_get_product_reviews",
    label: "Get product reviews",
    group: "Products",
    description:
      "Fetch customer reviews for a product — ratings, verified purchase status, and review text."
  },
  {
    name: "woocommerce_check_stock",
    label: "Check stock",
    group: "Products",
    description:
      "Real-time inventory check for a product or variation — returns quantity and stock status."
  },
  {
    name: "woocommerce_get_categories",
    label: "Get categories",
    group: "Products",
    description:
      "List all product categories to help customers browse and navigate the catalog."
  },
  {
    name: "woocommerce_get_top_sellers",
    label: "Get top sellers",
    group: "Products",
    description:
      "Retrieve best-selling products by sales volume — great for recommendations."
  },
  // Orders (7)
  {
    name: "woocommerce_get_order",
    label: "Get order details",
    group: "Orders",
    description:
      "Full order details by ID — status, items, totals, shipping method, and tracking."
  },
  {
    name: "woocommerce_search_orders",
    label: "Search orders",
    group: "Orders",
    description: "Find orders by customer email, order number, or name."
  },
  {
    name: "woocommerce_get_customer_orders",
    label: "Get customer orders",
    group: "Orders",
    description:
      "List all orders for a customer email — includes status and totals for each."
  },
  {
    name: "woocommerce_cancel_order",
    label: "Cancel order",
    group: "Orders",
    description:
      "Cancel an order (only eligible for pending/on-hold orders). Always confirm with customer first."
  },
  {
    name: "woocommerce_update_order_status",
    label: "Update order status",
    group: "Orders",
    description:
      "Update order status to: pending, processing, on-hold, completed, cancelled, or refunded."
  },
  {
    name: "woocommerce_get_order_notes",
    label: "Get order notes",
    group: "Orders",
    description:
      "Retrieve communication history and internal notes attached to an order."
  },
  {
    name: "woocommerce_create_order_note",
    label: "Add order note",
    group: "Orders",
    description:
      "Add a note to an order — visible to customer or internal only."
  },
  // Customers & Refunds (3)
  {
    name: "woocommerce_get_customer",
    label: "Get customer",
    group: "Customers & Refunds",
    description:
      "Look up a customer by email — returns name, order count, lifetime spend, and billing address."
  },
  {
    name: "woocommerce_get_refunds",
    label: "Get refunds",
    group: "Customers & Refunds",
    description:
      "Check existing refunds on an order — amounts, dates, and items refunded."
  },
  {
    name: "woocommerce_create_refund",
    label: "Create refund",
    group: "Customers & Refunds",
    description:
      "Process a partial or full refund on an order. Always confirm amount with customer first."
  },
  // Cart & Discounts (2)
  {
    name: "woocommerce_build_cart_link",
    label: "Build cart link",
    group: "Cart & Discounts",
    description:
      "Generate a direct WooCommerce cart URL pre-loaded with specified products and quantities."
  },
  {
    name: "woocommerce_validate_coupon",
    label: "Validate coupon",
    group: "Cart & Discounts",
    description:
      "Check if a coupon code is valid — returns discount type, value, and expiry date."
  },
  // Store & Shipping (5)
  {
    name: "woocommerce_get_shipping_methods",
    label: "Get shipping methods",
    group: "Store & Shipping",
    description:
      "List available shipping methods and rates by zone — flat rate, free shipping, local pickup."
  },
  {
    name: "woocommerce_get_payment_gateways",
    label: "Get payment gateways",
    group: "Store & Shipping",
    description:
      "List enabled payment methods (Stripe, PayPal, etc.) accepted at checkout."
  },
  {
    name: "woocommerce_get_tax_rates",
    label: "Get tax rates",
    group: "Store & Shipping",
    description:
      "Retrieve tax rates applicable to a location — useful for pre-purchase tax questions."
  },
  {
    name: "woocommerce_get_store_settings",
    label: "Get store settings",
    group: "Store & Shipping",
    description:
      "Store name, currency, timezone, and other configuration details."
  },
  {
    name: "woocommerce_get_sales_report",
    label: "Get sales report",
    group: "Store & Shipping",
    description:
      "Sales summary by date range — total revenue, orders, and top-selling items."
  },
  // Compare + Intelligence + Recommendations + Discount (4)
  {
    name: "woocommerce_compare_products",
    label: "Compare products",
    group: "Products",
    description:
      "Compare two WooCommerce products side by side — prices, stock, variants, and key differences. Triggered by 'what's the difference between X and Y?'"
  },
  {
    name: "woocommerce_customer_insights",
    label: "Customer intelligence",
    group: "Customers & Refunds",
    description:
      "Calculate lifetime value, VIP status (VIP/Loyal/Returning/New), purchase frequency, and top products for any customer. Gives AI an engagement strategy before the first message."
  },
  {
    name: "woocommerce_get_recommendations",
    label: "Product recommendations",
    group: "Products",
    description:
      "Returns up to 5 products from the same WooCommerce category as a source product. Use for 'what else goes with X?', 'show me similar products', or proactive upselling."
  },
  {
    name: "woocommerce_create_coupon",
    label: "Create coupon",
    group: "Cart & Discounts",
    description:
      "Creates a real-time WooCommerce coupon (percentage, fixed cart, or free shipping) with configurable usage limit and expiry. Use for cart recovery, VIP rewards, or price-match."
  }
];

const WOO_AI_TOOL_GROUPS = [
  "Products",
  "Orders",
  "Customers & Refunds",
  "Cart & Discounts",
  "Store & Shipping"
];

// ─── Capabilities ──────────────────────────────────────────────────────────────

const WOO_CAPABILITIES = [
  {
    title: "Product catalog search",
    icon: "ShoppingCart",
    items: [
      "Search 24/7 across your entire WooCommerce catalog by keyword, SKU, or category",
      "Show real-time stock levels, pricing, and all available variations (size, color, etc.)",
      "Surface customer reviews and ratings to help buyers make confident decisions",
      "Recommend best-selling products and in-stock alternatives when items are unavailable",
      "Compare any two products side by side — price, variants, stock, and ratings"
    ]
  },
  {
    title: "Order management",
    icon: "Package",
    items: [
      "Look up any order by order number or customer email — status, items, totals, and tracking",
      "Process cancellations and partial or full refunds directly via the WooCommerce REST API",
      "Add internal order notes to document customer interactions and resolution steps",
      "Update order status across the full WooCommerce status lifecycle"
    ]
  },
  {
    title: "Customer account lookup",
    icon: "User",
    items: [
      "Retrieve full customer profiles by email — order history, lifetime spend, and billing address",
      "Show a customer's complete order list at a glance — useful for returns and account queries",
      "Generate personalized cart links with products pre-loaded for a frictionless checkout"
    ]
  },
  {
    title: "Pre-sales & checkout support",
    icon: "CreditCard",
    items: [
      "Answer shipping cost and method questions using live store configuration",
      "List accepted payment methods so customers know their options before adding to cart",
      "Validate coupon codes in real time — confirms discount, expiry, and eligibility",
      "Show tax rates by location for accurate pre-purchase cost estimates"
    ]
  },
  {
    title: "AI workflow tools",
    icon: "Bot",
    items: [
      "28 tools available to any AI workflow node on this site — no workflow configuration required",
      "Works with any WooCommerce store via the REST API — no plugin install needed",
      "The AI decides when to call WooCommerce based on conversation context",
      "All returned data available as workflow variables for HTTP POST nodes and agent sidebar"
    ]
  }
];

// ─── How It Works Card ─────────────────────────────────────────────────────────

function WooHowItWorksCard() {
  const steps = [
    {
      number: "1",
      title: "Enter your store credentials",
      desc: "Paste your WordPress site URL, Consumer Key, and Consumer Secret from your WooCommerce API settings. No plugin required — Velaro uses the WooCommerce REST API.",
      color: "bg-purple-600"
    },
    {
      number: "2",
      title: "28 AI tools activate instantly",
      desc: "Every AI workflow bot on your site gains access to 28 WooCommerce tools — products, orders, customers, refunds, shipping, cart links, discount coupons, and customer intelligence.",
      color: "bg-blue-600"
    },
    {
      number: "3",
      title: "Data surfaces everywhere it matters",
      desc: "Store data appears in the agent sidebar during live chats, as workflow variables in HTTP POST nodes, and in the bot conversation — giving agents and AI full context.",
      color: "bg-green-600"
    }
  ];

  return (
    <SectionCard
      title="How it works"
      description="Connect once — AI handles orders, products, and customers automatically."
    >
      <div className="flex flex-col gap-4 sm:flex-row">
        {steps.map((step) => (
          <div key={step.number} className="flex-1 flex flex-col gap-2">
            <div
              className={`w-8 h-8 rounded-full ${step.color} text-white flex items-center justify-center font-bold text-sm shrink-0`}
            >
              {step.number}
            </div>
            <div className="font-semibold text-zinc-700 text-sm">
              {step.title}
            </div>
            <div className="text-xs text-zinc-500 leading-relaxed">
              {step.desc}
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

// ─── Capabilities Panel ────────────────────────────────────────────────────────

function WooCapabilitiesPanel() {
  const [open, setOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Set<number>>(new Set([0]));

  function toggleSection(i: number) {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  return (
    <SectionCard
      title="What's included"
      description={`${WOO_CAPABILITIES.length} features · 28 AI tools`}
      action={
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="text-sm text-(--ds-primary) font-medium hover:underline"
          aria-expanded={open}
        >
          {open ? "Hide" : "Show"}
        </button>
      }
    >
      {open && (
        <>
          <Divider className="my-0 mb-4" />
          <div className="space-y-2">
            {WOO_CAPABILITIES.map((cap, i) => (
              <div
                key={i}
                className="rounded-lg border border-zinc-100 overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => toggleSection(i)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-zinc-50"
                >
                  <div className="flex items-center gap-3">
                    <IconByName name={cap.icon} className="w-4 h-4" />
                    <span className="text-sm font-medium text-zinc-700">
                      {cap.title}
                    </span>
                  </div>
                  <svg
                    className={`w-3.5 h-3.5 text-zinc-400 transition-transform shrink-0 ${openSections.has(i) ? "rotate-180" : ""}`}
                    viewBox="0 0 16 16"
                    fill="currentColor"
                  >
                    <path d="M8 10.5L3 5.5H13L8 10.5Z" />
                  </svg>
                </button>
                {openSections.has(i) && (
                  <div className="px-4 pb-3 space-y-1.5 border-t border-zinc-50">
                    {cap.items.map((item, j) => (
                      <div key={j} className="flex items-start gap-2 pt-1.5">
                        <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
                        <span className="text-xs text-zinc-600">{item}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </SectionCard>
  );
}

// ─── AI Tools Panel ────────────────────────────────────────────────────────────

interface WooAIToolsPanelProps {
  isConnected: boolean;
  onLaunchWizard: () => void;
}

function WooAIToolsPanel({
  isConnected,
  onLaunchWizard
}: WooAIToolsPanelProps) {
  return (
    <SectionCard
      title="AI Tools"
      description="28 tools available to any AI workflow bot. No configuration needed — the AI decides when to call WooCommerce."
      action={
        isConnected ? (
          <button
            type="button"
            onClick={onLaunchWizard}
            className="text-sm px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 whitespace-nowrap font-medium shadow-sm"
          >
            Launch Starter Wizard
          </button>
        ) : undefined
      }
    >
      <div className="flex items-center gap-2 mb-4">
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded-full ${isConnected ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}
        >
          {isConnected ? "Active" : "Connect store to activate"}
        </span>
      </div>
      <Divider />
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">
            Available AI tools
          </div>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
            28 tools
          </span>
        </div>
        {WOO_AI_TOOL_GROUPS.map((group) => (
          <div key={group} className="mb-4">
            <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wide mb-2">
              {group}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {WOO_AI_TOOLS.filter((t) => t.group === group).map((tool) => (
                <div
                  key={tool.name}
                  className={`flex gap-3 p-3 rounded-lg border ${isConnected ? "border-green-200 bg-green-50" : "border-zinc-200 bg-zinc-50"}`}
                >
                  <div
                    className={`mt-0.5 w-4 h-4 rounded-full shrink-0 flex items-center justify-center ${isConnected ? "bg-green-500" : "bg-zinc-300"}`}
                  >
                    {isConnected && (
                      <svg viewBox="0 0 10 10" width="8" height="8" fill="none">
                        <path
                          d="M1.5 5L3.5 7L8.5 3"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-zinc-700">
                      {tool.label}
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      {tool.description}
                    </div>
                    <code className="text-[10px] text-zinc-400 mt-1 block">
                      {tool.name}
                    </code>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Divider className="mt-8 mb-8" />
      <SkillControlsPanel integration="woocommerce" skills={WOO_AI_TOOLS} />
    </SectionCard>
  );
}

// ─── How To Get Keys Accordion ─────────────────────────────────────────────────

function HowToGetKeysAccordion() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-zinc-200 mt-4">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-zinc-50 rounded-lg"
        aria-expanded={open}
      >
        <span className="text-sm font-medium text-zinc-700">
          How to get your API keys
        </span>
        <svg
          className={`w-4 h-4 text-zinc-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`}
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M8 10.5L3 5.5H13L8 10.5Z" />
        </svg>
      </button>
      {open && (
        <>
          <Divider className="my-0" />
          <div className="px-4 pb-4 pt-3">
            <ol className="space-y-2.5">
              {[
                "Go to your WordPress admin dashboard",
                <>
                  Navigate to <strong>WooCommerce &rarr; Settings</strong>
                </>,
                <>
                  Click the <strong>Advanced</strong> tab, then{" "}
                  <strong>REST API</strong>
                </>,
                <>
                  Click the <strong>Add key</strong> button
                </>,
                <>
                  Set Description: <strong>Velaro Chat Bot</strong>, User:
                  select an admin, Permissions: <strong>Read/Write</strong>
                </>,
                <>
                  Click <strong>Generate API key</strong>
                </>,
                "Copy the Consumer Key and Consumer Secret — they only show once!"
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-(--ds-primary) text-white text-xs flex items-center justify-center font-bold shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-sm text-zinc-600">{step}</span>
                </li>
              ))}
            </ol>
            <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
              <strong>Important:</strong> The Consumer Key and Consumer Secret
              are shown only once after generation. Copy them immediately and
              keep them secure — treat them like passwords.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Connect Store Tab ─────────────────────────────────────────────────────────

function ConnectStoreTab({ onConnected }: { onConnected?: () => void }) {
  const api = useApi();
  const toastAlert = useToastAlert();
  const { teams } = useTeams();
  const [form, setForm] = useState<ConnectForm>({
    storeUrl: "",
    consumerKey: "",
    consumerSecret: ""
  });
  const [saving, setSaving] = useState(false);
  const [connected, setConnected] = useState(false);
  const [connectedUrl, setConnectedUrl] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [disabledAt, setDisabledAt] = useState<string | null>(null);
  const [disabledBy, setDisabledBy] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [selectedTeamId, setSelectedTeamId] = useState<number | "">("");
  const [savingTeam, setSavingTeam] = useState(false);
  const [teamSaved, setTeamSaved] = useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const resp = await api.admin.get("WooCommerce/Status");
        const data = await resp.json();
        if (data.connected) {
          setConnected(true);
          setConnectedUrl(data.storeUrl ?? "");
          setIsActive(data.isActive ?? true);
          setDisabledAt(data.disabledAt ?? null);
          setDisabledBy(data.disabledBy ?? null);
          setForm((f) => ({ ...f, storeUrl: data.storeUrl ?? "" }));
        }
      } catch (err) {
        console.error("WooCommerce:", err);
      } finally {
        setLoadingStatus(false);
      }
    })();
  }, []);

  async function handleToggleActive() {
    try {
      const resp = await api.admin.post("WooCommerce/toggle-active", {});
      const data = await resp.json();
      setIsActive(data.isActive ?? true);
      setDisabledAt(data.disabledAt ?? null);
      setDisabledBy(data.disabledBy ?? null);
    } catch (err) {
      console.error("WooCommerce:", err);
    }
  }

  async function handleSave() {
    if (!form.storeUrl.trim()) {
      toastAlert.displayToast(
        Severity.Warning,
        "Enter your WordPress site URL."
      );
      return;
    }
    if (!form.consumerKey.trim()) {
      toastAlert.displayToast(Severity.Warning, "Enter your Consumer Key.");
      return;
    }
    if (!form.consumerSecret.trim()) {
      toastAlert.displayToast(Severity.Warning, "Enter your Consumer Secret.");
      return;
    }
    setSaving(true);
    setTestResult(null);
    try {
      const saveResp = await api.admin.post("WooCommerce/SaveConfig", {
        storeUrl: form.storeUrl.trim(),
        consumerKey: form.consumerKey.trim(),
        consumerSecret: form.consumerSecret.trim()
      });
      const saveData = await saveResp.json();
      if (!saveData.success) {
        toastAlert.displayToast(
          Severity.Error,
          saveData.message || "Failed to save."
        );
        return;
      }

      // Test the connection
      const testResp = await api.admin.post("WooCommerce/TestConnection", {});
      const testData = await testResp.json();
      setTestResult({ success: testData.success, message: testData.message });

      if (testData.success) {
        setConnected(true);
        setConnectedUrl(form.storeUrl.trim());
        onConnected?.();
        ensureDefaultRoutingWorkflow(api.messaging);
        toastAlert.displayToast(
          Severity.Success,
          "WooCommerce store connected successfully!"
        );
      } else {
        toastAlert.displayToast(
          Severity.Warning,
          "Config saved but connection test failed — check your credentials."
        );
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to save config.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveTeam() {
    if (selectedTeamId === "") return;
    setSavingTeam(true);
    try {
      const defaultResp = await api.messaging.get("Bundle/default");
      if (defaultResp.ok) {
        const bundle = await defaultResp.json();
        if (bundle?.id) {
          await api.messaging.put(`Bundle/update/${bundle.id}`, {
            ...bundle,
            defaultTeamId: selectedTeamId
          });
        }
      }
      setTeamSaved(true);
    } catch (err) {
      console.error("Failed to set default team:", err);
    } finally {
      setSavingTeam(false);
    }
  }

  if (loadingStatus) {
    return (
      <div className="p-8 text-center text-zinc-400 text-sm">Loading...</div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Connected banner */}
      {connected && (
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
          <div
            className="w-2.5 h-2.5 rounded-full bg-green-500 shrink-0"
            aria-hidden="true"
          />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-green-800">
              Store connected
            </div>
            <div className="text-xs text-green-600 truncate">
              {connectedUrl}
            </div>
          </div>
          <button
            type="button"
            onClick={handleToggleActive}
            className={`text-xs font-medium px-2 py-1 rounded-full mr-2 ${isActive ? "bg-blue-100 text-blue-700 hover:bg-blue-200" : "bg-amber-100 text-amber-700 hover:bg-amber-200"}`}
            title={
              isActive
                ? "Click to pause integration"
                : "Click to resume integration"
            }
          >
            {isActive ? "Active" : "Paused"}
          </button>
          <button
            type="button"
            onClick={() => setConnected(false)}
            className="text-xs text-green-700 hover:text-green-900 font-medium"
          >
            Update keys
          </button>
        </div>
      )}
      {/* Quick team routing setup — only shown after first connect if no team saved yet */}
      {connected && !teamSaved && Array.isArray(teams) && teams.length > 0 && (
        <div className="p-4 bg-zinc-50 rounded-xl space-y-3">
          <div className="text-sm font-medium text-zinc-700">
            Route incoming chats to:
          </div>
          <div className="flex items-center gap-2">
            <select
              className="flex-1 input text-sm"
              value={selectedTeamId}
              onChange={(e) =>
                setSelectedTeamId(
                  e.target.value === "" ? "" : Number(e.target.value)
                )
              }
            >
              <option value="">Choose a team…</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <PrimaryButton
              onClick={handleSaveTeam}
              disabled={selectedTeamId === "" || savingTeam}
            >
              {savingTeam ? "Saving…" : "Set team"}
            </PrimaryButton>
          </div>
          <p className="text-xs text-zinc-400">
            Chats that don't match a canvas workflow fall back to this team.
            Change it any time in{" "}
            <Link to="/deployment" className="underline">
              Chat Experience
            </Link>
            .
          </p>
        </div>
      )}
      {connected && teamSaved && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
          <Check className="w-4 h-4 text-green-600 shrink-0" aria-hidden />
          Default routing team set.{" "}
          <Link to="/workflows" className="underline font-medium ml-1">
            Open Workflows →
          </Link>
        </div>
      )}

      {/* Disabled banner */}
      {connected && !isActive && (
        <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-800 flex items-center gap-2">
          <svg
            viewBox="0 0 20 20"
            width="16"
            height="16"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
              clipRule="evenodd"
            />
          </svg>
          <span>
            Integration disabled on{" "}
            {disabledAt
              ? new Date(disabledAt).toLocaleDateString()
              : "unknown date"}
            {disabledBy ? ` by ${disabledBy}` : ""} — bot will not use this
            store's data.
          </span>
        </div>
      )}

      {/* Connection form */}
      <SectionCard
        title={
          connected
            ? "Update WooCommerce credentials"
            : "Connect your WooCommerce store"
        }
        description="WooCommerce uses REST API keys for authentication. Generate a key pair in your WordPress admin and paste them below. No plugin or OAuth redirect required."
        action={
          <PrimaryButton onClick={handleSave} disabled={saving}>
            {saving ? "Testing connection..." : "Save and Test Connection"}
          </PrimaryButton>
        }
      >
        <div>
          <div className="space-y-4">
            <div>
              <label
                className="block text-sm font-medium text-zinc-700 mb-1"
                htmlFor="woo-store-url"
              >
                WordPress Site URL
              </label>
              <input
                id="woo-store-url"
                type="url"
                value={form.storeUrl}
                onChange={(e) =>
                  setForm((f) => ({ ...f, storeUrl: e.target.value }))
                }
                placeholder="https://mystore.com"
                className="w-full text-sm border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-300"
              />
              <p className="text-xs text-zinc-400 mt-1">
                Your WordPress site root URL — not the WooCommerce admin URL.
              </p>
            </div>

            <div>
              <label
                className="block text-sm font-medium text-zinc-700 mb-1"
                htmlFor="woo-consumer-key"
              >
                Consumer Key
              </label>
              <input
                id="woo-consumer-key"
                type="text"
                value={form.consumerKey}
                onChange={(e) =>
                  setForm((f) => ({ ...f, consumerKey: e.target.value }))
                }
                placeholder="ck_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                className="w-full text-sm border border-zinc-200 rounded-lg px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-300"
              />
            </div>

            <div>
              <label
                className="block text-sm font-medium text-zinc-700 mb-1"
                htmlFor="woo-consumer-secret"
              >
                Consumer Secret
              </label>
              <input
                id="woo-consumer-secret"
                type="password"
                value={form.consumerSecret}
                onChange={(e) =>
                  setForm((f) => ({ ...f, consumerSecret: e.target.value }))
                }
                placeholder="cs_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                className="w-full text-sm border border-zinc-200 rounded-lg px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-300"
              />
            </div>
          </div>

          <HowToGetKeysAccordion />

          {testResult && (
            <div
              className={`mt-4 p-3 rounded-lg text-sm ${
                testResult.success
                  ? "bg-green-50 border border-green-200 text-green-700"
                  : "bg-amber-50 border border-amber-200 text-amber-700"
              }`}
            >
              {testResult.message}
            </div>
          )}
        </div>
      </SectionCard>

      {/* Why WooCommerce card */}
      <SectionCard title="Why WooCommerce?">
        <div className="font-semibold text-zinc-700 mb-3 flex items-center gap-2">
          <svg
            viewBox="0 0 20 20"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            aria-hidden="true"
            className="text-purple-600"
          >
            <path
              d="M3 3h2l2.5 10h8l2-6H7.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="9" cy="16" r="1" />
            <circle cx="15" cy="16" r="1" />
          </svg>
          Why WooCommerce?
        </div>
        <div className="space-y-3">
          {[
            {
              icon: (
                <svg
                  viewBox="0 0 16 16"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                  className="text-green-500 shrink-0 mt-0.5"
                >
                  <path
                    d="M3 8l3 3 7-7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ),
              text: "WooCommerce powers 40% of all online stores worldwide — the largest e-commerce platform by install count"
            },
            {
              icon: (
                <svg
                  viewBox="0 0 16 16"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                  className="text-green-500 shrink-0 mt-0.5"
                >
                  <path
                    d="M3 8l3 3 7-7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ),
              text: "Works with any WordPress site — no Shopify or BigCommerce subscription needed, no platform lock-in"
            },
            {
              icon: (
                <svg
                  viewBox="0 0 16 16"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                  className="text-green-500 shrink-0 mt-0.5"
                >
                  <path
                    d="M3 8l3 3 7-7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ),
              text: "Full product catalog search, order lookup, and customer history in every chat — bot handles support without agents"
            }
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-2.5">
              {item.icon}
              <span className="text-sm text-zinc-600">{item.text}</span>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

// ─── Data Mapping ─────────────────────────────────────────────────────────────

function WooCommerceDataMapping() {
  const FIELD_GROUPS = [
    {
      label: "Customer Fields",
      icon: (
        <svg
          viewBox="0 0 16 16"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <circle cx="8" cy="5" r="3" />
          <path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6" strokeLinecap="round" />
        </svg>
      ),
      fields: [
        {
          wc: "customer.email",
          velaro: "contact.email",
          desc: "Primary customer email address"
        },
        {
          wc: "customer.first_name",
          velaro: "contact.firstName",
          desc: "Customer first name"
        },
        {
          wc: "customer.last_name",
          velaro: "contact.lastName",
          desc: "Customer last name"
        },
        {
          wc: "customer.billing.phone",
          velaro: "contact.phone",
          desc: "Billing phone number"
        },
        {
          wc: "customer.billing.city",
          velaro: "contact.city",
          desc: "Billing city"
        },
        {
          wc: "customer.billing.country",
          velaro: "contact.country",
          desc: "Billing country code"
        },
        {
          wc: "customer.orders_count",
          velaro: "contact.ordersCount",
          desc: "Total number of orders"
        },
        {
          wc: "customer.total_spent",
          velaro: "contact.totalSpent",
          desc: "Lifetime spend (currency string)"
        }
      ]
    },
    {
      label: "Order Fields",
      icon: (
        <svg
          viewBox="0 0 16 16"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <rect x="2" y="3" width="12" height="10" rx="1.5" />
          <path d="M5 7h6M5 10h4" strokeLinecap="round" />
        </svg>
      ),
      fields: [
        {
          wc: "order.id",
          velaro: "order.id",
          desc: "WooCommerce order number"
        },
        {
          wc: "order.status",
          velaro: "order.status",
          desc: "pending / processing / completed / refunded"
        },
        {
          wc: "order.total",
          velaro: "order.total",
          desc: "Order total including tax and shipping"
        },
        {
          wc: "order.date_created",
          velaro: "order.dateCreated",
          desc: "ISO 8601 order creation date"
        },
        {
          wc: "order.billing.email",
          velaro: "order.billingEmail",
          desc: "Email used at checkout"
        },
        {
          wc: "order.shipping_lines[0].method_title",
          velaro: "order.shippingMethod",
          desc: "Shipping method name"
        },
        {
          wc: "order.line_items[0].name",
          velaro: "order.firstItemName",
          desc: "First line item product name"
        },
        {
          wc: "order.coupon_lines[0].code",
          velaro: "order.couponCode",
          desc: "Applied coupon code (if any)"
        }
      ]
    },
    {
      label: "Product Fields",
      icon: (
        <svg
          viewBox="0 0 16 16"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <path
            d="M2 2h3l2 8h6l2-6H7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="7" cy="13" r="1" />
          <circle cx="11" cy="13" r="1" />
        </svg>
      ),
      fields: [
        {
          wc: "product.id",
          velaro: "product.id",
          desc: "WooCommerce product ID"
        },
        {
          wc: "product.name",
          velaro: "product.name",
          desc: "Product display name"
        },
        { wc: "product.sku", velaro: "product.sku", desc: "Product SKU" },
        {
          wc: "product.price",
          velaro: "product.price",
          desc: "Current selling price"
        },
        {
          wc: "product.regular_price",
          velaro: "product.regularPrice",
          desc: "Price before any sale"
        },
        {
          wc: "product.stock_status",
          velaro: "product.stockStatus",
          desc: "instock / outofstock / onbackorder"
        },
        {
          wc: "product.stock_quantity",
          velaro: "product.stockQty",
          desc: "Units currently in stock"
        },
        {
          wc: "product.permalink",
          velaro: "product.url",
          desc: "Full URL to the product page"
        }
      ]
    }
  ];

  return (
    <div className="space-y-4">
      <SectionCard
        title="Field Reference — WooCommerce"
        description="All WooCommerce REST API fields available in every connected store. Use the Velaro variable name in HTTP POST nodes or workflow conditions to forward WooCommerce data to other systems."
      >
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-700 mb-5">
          <strong>How it works:</strong> When an AI skill calls the WooCommerce
          API, the returned data is available as workflow variables. For
          example, after{" "}
          <code className="font-mono bg-blue-100 px-1 rounded">
            woocommerce_get_order
          </code>
          , you can reference the order total in an HTTP POST as{" "}
          <code className="font-mono bg-blue-100 px-1 rounded">
            {"{{order.total}}"}
          </code>
          .
        </div>

        <div className="space-y-5">
          {FIELD_GROUPS.map((group) => (
            <div key={group.label}>
              <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-zinc-700">
                <span className="text-purple-600">{group.icon}</span>
                {group.label}
              </div>
              <div className="rounded-xl border border-zinc-200 overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-zinc-200">
                      <th className="text-left px-4 py-2 font-medium text-zinc-600 w-2/5">
                        WooCommerce field
                      </th>
                      <th className="text-left px-4 py-2 font-medium text-zinc-600 w-1/4">
                        Velaro variable
                      </th>
                      <th className="text-left px-4 py-2 font-medium text-zinc-600">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.fields.map((f, i) => (
                      <tr
                        key={i}
                        className={`border-b border-zinc-100 last:border-b-0 ${i % 2 === 0 ? "bg-white" : "bg-zinc-50/50"}`}
                      >
                        <td className="px-4 py-2 font-mono text-zinc-600">
                          {f.wc}
                        </td>
                        <td className="px-4 py-2 font-mono text-purple-700">{`{{${f.velaro}}}`}</td>
                        <td className="px-4 py-2 text-zinc-500">{f.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 p-3 bg-zinc-50 rounded-lg text-xs text-zinc-600">
          <strong>To use in workflows:</strong> Wrap the Velaro variable name in
          double curly braces in any workflow node's text, URL, or JSON body
          field — e.g.{" "}
          <code className="font-mono bg-zinc-200 px-1 rounded">
            {"{{order.status}}"}
          </code>
          . Variables are only available after the AI skill that fetches the
          data has run.
        </div>
      </SectionCard>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function WooCommerce() {
  const { enableEcommerceSync } = useAdminFeatures();
  const [activeTab, setActiveTab] = useState<
    | "overview"
    | "config"
    | "skills"
    | "mapping"
    | "log"
    | "shoppers"
    | "subscription"
  >("overview");
  const [showWizard, setShowWizard] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [storeUrl, setStoreUrl] = useState("");
  const api = useApi();

  React.useEffect(() => {
    (async () => {
      try {
        const resp = await api.admin.get("WooCommerce/Status");
        const data = await resp.json();
        setIsConnected(!!data.connected);
        setStoreUrl(data.storeUrl ?? "");
      } catch (err) {
        console.error("WooCommerce:", err);
      }
    })();
  }, [api.admin]);

  const tabsJSX = (
    <>
      <IntegrationTab
        active={activeTab === "overview"}
        onClick={() => setActiveTab("overview")}
      >
        Overview
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "config"}
        onClick={() => setActiveTab("config")}
      >
        Configuration
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "skills"}
        onClick={() => setActiveTab("skills")}
      >
        AI Skills
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "mapping"}
        onClick={() => setActiveTab("mapping")}
      >
        Field Reference
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "subscription"}
        onClick={() => setActiveTab("subscription")}
      >
        Subscription
      </IntegrationTab>
      {enableEcommerceSync && (
        <IntegrationTab
          active={activeTab === "shoppers"}
          onClick={() => setActiveTab("shoppers")}
        >
          Shoppers
        </IntegrationTab>
      )}
      <IntegrationTab
        active={activeTab === "log"}
        onClick={() => setActiveTab("log")}
      >
        Activity Log
      </IntegrationTab>
    </>
  );

  return (
    <StickyRouteHeader title="WooCommerce" tabs={tabsJSX}>
      {showWizard && (
        <StarterWorkflowWizard
          integration="woocommerce"
          websiteUrl={storeUrl || undefined}
          onClose={() => setShowWizard(false)}
        />
      )}

      {activeTab === "overview" && (
        <div className="space-y-0">
          <WooHowItWorksCard />
          <WooCapabilitiesPanel />
          <WooCommerceBotKnowledge />
        </div>
      )}

      {activeTab === "config" && (
        <div>
          {isConnected && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex flex-col gap-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-medium text-blue-800 mb-0.5">
                    Deploy the chat widget to your store
                  </div>
                  <div className="text-xs text-blue-600">
                    Your WooCommerce integration is connected — grab your embed
                    code from Deployments to go live.
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link
                    to="/chat_window"
                    className="text-xs px-3 py-1.5 rounded-lg bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100 transition"
                  >
                    Chat Window
                  </Link>
                  <Link
                    to="/deployment"
                    className="text-xs px-3 py-1.5 rounded-lg bg-(--ds-primary) hover:opacity-90 text-white transition"
                  >
                    Get Embed Code →
                  </Link>
                </div>
              </div>
              <div className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2 text-xs text-blue-700 border border-blue-100">
                <Lightbulb className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  <strong>Tip:</strong> You can also create a{" "}
                  <strong>Knowledge Base Q&A</strong> page widget from{" "}
                  <Link
                    to="/deployment"
                    className="underline hover:text-blue-900"
                  >
                    Deployments → Page Widgets
                  </Link>{" "}
                  for a lightweight, AI-only Q&A experience — no live agent
                  needed.
                </span>
              </div>
            </div>
          )}
          <ConnectStoreTab onConnected={() => setIsConnected(true)} />
        </div>
      )}

      {activeTab === "skills" && (
        <WooAIToolsPanel
          isConnected={isConnected}
          onLaunchWizard={() => setShowWizard(true)}
        />
      )}

      {activeTab === "mapping" && <WooCommerceDataMapping />}

      {activeTab === "subscription" && <WooCommerceBilling />}

      {activeTab === "shoppers" && enableEcommerceSync && (
        <SectionCard
          title="Shopper Profiles — WooCommerce"
          description="All customers synced from your WooCommerce store. Data updates hourly."
        >
          <ShopperProfileTab platform="WooCommerce" />
        </SectionCard>
      )}

      {activeTab === "log" && (
        <IntegrationActivityLog integrationSlug="WooCommerce" />
      )}
    </StickyRouteHeader>
  );
}

export function BackToIntegrationLandingWooCommerce() {
  const navigate = useNavigate();
  return (
    <div
      className="flex cursor-pointer"
      onClick={() => navigate("/integrations")}
      role="button"
      aria-label="Back to integrations"
    >
      <IconClose1 className="h-6 w-6" />
    </div>
  );
}
